import { useEffect, useRef } from 'react'
import { AlertTriangle } from 'lucide-react'
import clsx from 'clsx'

import { useConfirmacion } from '@/lib/confirmar'

/**
 * El diálogo que pregunta antes de una acción que no se deshace.
 *
 * Se monta UNA vez por runtime (el layout del local y el panel de plataforma,
 * los mismos dos lugares que montan el cajón de ayuda) y lo dispara
 * `confirmar()` desde donde sea.
 *
 * Es un `<dialog>` modal y no un div, por lo mismo que el cajón de ayuda: un
 * modal vive en la capa superior del navegador, así que cualquier hermano
 * queda por debajo, invisible y sin poder recibir clics. Y esto sale de
 * adentro de un modal todo el tiempo: el error que devuelve el servidor al
 * guardar un formulario —que es un `Modal`— se muestra con `avisar()`, así
 * que un div quedaría tapado por el propio formulario que lo disparó.
 */
export function Confirmacion() {
  const pedido = useConfirmacion((s) => s.pedido)
  const responder = useConfirmacion((s) => s.responder)

  const ref = useRef<HTMLDialogElement>(null)
  const cancelar = useRef<HTMLButtonElement>(null)
  const aceptar = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const dialogo = ref.current
    if (!dialogo) return

    if (pedido && !dialogo.open) dialogo.showModal()
    if (!pedido && dialogo.open) dialogo.close()
  }, [pedido])

  // El foco arranca en CANCELAR y no en el botón que hace la cosa.
  //
  // Es la diferencia que importa en una pantalla táctil con gente esperando:
  // un Enter de más —el que venía de confirmar el formulario anterior— no
  // puede dar de baja un producto. El `confirm()` del navegador enfoca el OK,
  // y esta es una de las razones para reemplazarlo.
  useEffect(() => {
    if (!pedido) return

    // En un aviso no hay nada que decidir, así que el foco va al único botón.
    if (pedido.soloAviso || pedido.enfocarConfirmar) aceptar.current?.focus()
    else cancelar.current?.focus()
  }, [pedido])

  useEffect(() => {
    const dialogo = ref.current
    if (!dialogo) return

    // El evento `cancel` es Escape. Se previene el cierre nativo para que el
    // estado y el diálogo no queden desincronizados, y se responde NO: no
    // contestar dejaría al que espera colgado para siempre.
    const alCancelar = (e: Event) => {
      e.preventDefault()
      responder(false)
    }

    dialogo.addEventListener('cancel', alCancelar)
    return () => dialogo.removeEventListener('cancel', alCancelar)
  }, [responder])

  return (
    <dialog
      ref={ref}
      aria-labelledby="confirmacion-titulo"
      className={clsx(
        'w-[calc(100vw-2rem)] max-w-[440px] rounded-xl border border-borde bg-crema p-0',
        'shadow-elevada backdrop:bg-tinta/45 backdrop:backdrop-blur-[2px] animate-entrar',

        // `hidden open:flex`: sin el atributo `open`, la hoja del navegador lo
        // esconde con un display:none de MENOS especificidad que una clase de
        // utilidad, así que un `flex` suelto lo mostraría siempre.
        'hidden flex-col open:flex',
      )}
      // Click en el fondo: cuenta como cancelar. El backdrop no es un elemento
      // aparte, así que se compara el target con el propio dialog.
      onClick={(e) => {
        if (e.target === ref.current) responder(false)
      }}
    >
      {pedido && (
        <>
          <div className="flex items-start gap-3.5 px-5 pb-4 pt-5">
            {pedido.peligrosa && (
              <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-rojo/10 text-rojo">
                <AlertTriangle className="h-[18px] w-[18px]" />
              </span>
            )}

            <div className="min-w-0">
              <h2
                id="confirmacion-titulo"
                className="text-[16.5px] font-bold leading-snug tracking-[-0.01em] text-tinta"
              >
                {pedido.titulo}
              </h2>

              {pedido.detalle && (
                <p className="mt-1.5 text-[13.5px] leading-relaxed text-tinta-tenue">
                  {pedido.detalle}
                </p>
              )}
            </div>
          </div>

          {/* Los botones van en el pie y el que confirma va a la DERECHA, como
              en el resto del sistema. `btn-toque` porque en el mostrador esto
              se aprieta con el dedo. */}
          <footer className="flex flex-wrap justify-end gap-2 border-t border-borde bg-crema-alt/60 px-5 py-3.5">
            {!pedido.soloAviso && (
              <button
                ref={cancelar}
                type="button"
                className="btn-fantasma btn-toque"
                onClick={() => responder(false)}
              >
                Cancelar
              </button>
            )}

            <button
              ref={aceptar}
              type="button"
              className={pedido.peligrosa ? 'btn-peligro btn-toque' : 'btn-primario btn-toque'}
              onClick={() => responder(true)}
            >
              {pedido.confirmar}
            </button>
          </footer>
        </>
      )}
    </dialog>
  )
}
